import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useNewBadge } from '@/hooks/useNewBadge';
import { usePermissions } from '@/hooks/usePermissions';
import { ROLE_LABELS, ROLE_COLORS } from '@/lib/roles';
import { LayoutDashboard, Table2, BarChart3, FileText, UserPlus, LogOut, User, Menu, X, ChevronsLeft, ChevronsRight, KeyRound, Loader2, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import HelpModal from '@/components/HelpModal';

const NAV_ITEMS = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/data', label: 'Attività', icon: Table2, showBadge: true },
  { to: '/plots', label: 'KPI Plot', icon: BarChart3, requiresPermission: 'canViewPlots' },
  { to: '/report', label: 'Report', icon: FileText, requiresPermission: 'canViewReport' },
  { to: '/inviti', label: 'Gestione Utenti', icon: UserPlus, requiresPermission: 'canInviteUsers' },
];

function SidebarContent({ user, role, permissions, newCount, onMarkSeen, onSignOut, onChangePassword, showChangePassword, onNavClick, collapsed, onToggle }) {
  const visibleNavItems = NAV_ITEMS.filter((item) => {
    if (!item.requiresPermission) return true;
    return permissions?.[item.requiresPermission] === true;
  });
  const roleLabel = ROLE_LABELS[role] || role;
  const roleColor = ROLE_COLORS[role] || { bg: 'bg-fw-light', text: 'text-fw-dark' };
  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className={`py-6 border-b border-white/10 ${collapsed ? 'px-2' : 'px-5'}`}>
        <NavLink
          to="/dashboard"
          className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'}`}
          data-testid="logo-link"
          onClick={onNavClick}
          title={collapsed ? 'ETO Support' : undefined}
        >
          {collapsed
            ? <span className="font-heading font-bold text-lg text-fw-yellow">ETO</span>
            : <span className="font-heading font-bold text-xl text-white tracking-wide">ETO Support</span>
          }
        </NavLink>
      </div>

      {/* Navigation */}
      <nav className={`flex-1 py-4 space-y-1 ${collapsed ? 'px-2' : 'px-3'}`} data-testid="main-nav">
        {visibleNavItems.map(({ to, label, icon: Icon, showBadge }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={`nav-${to.slice(1)}`}
            onClick={() => { onNavClick?.(); if (showBadge && newCount > 0) onMarkSeen?.(); }}
            title={collapsed ? label : undefined}
            className={({ isActive }) =>
              `flex items-center rounded-lg text-sm font-medium transition-colors duration-200 ${
                collapsed ? 'justify-center px-2 py-2.5' : 'gap-3 px-3 py-2.5'
              } ${
                isActive
                  ? 'bg-fw-yellow/15 text-fw-yellow'
                  : 'text-fw-subtle hover:bg-white/10 hover:text-fw-yellow'
              }`
            }
          >
            <div className="relative shrink-0">
              <Icon className="w-5 h-5" />
              {showBadge && newCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {newCount > 9 ? '9+' : newCount}
                </span>
              )}
            </div>
            {!collapsed && <span>{label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* User / Logout */}
      <div className={`py-4 border-t border-white/10 ${collapsed ? 'px-2' : 'px-3'}`}>
        {!collapsed && (
          <div className="px-3 py-2 mb-2" data-testid="user-info">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-white/15 shrink-0">
                <User className="w-4 h-4 text-fw-subtle" />
              </div>
              <span className="text-sm text-fw-subtle truncate max-w-[140px]">{user?.email}</span>
            </div>
            {role && (
              <Badge className={`mt-2 ml-11 ${roleColor.bg} ${roleColor.text} border-0 text-xs`}>
                {roleLabel}
              </Badge>
            )}
          </div>
        )}
        {collapsed && (
          <div className="flex justify-center mb-2" data-testid="user-info" title={`${user?.email} (${roleLabel})`}>
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-white/15">
              <User className="w-4 h-4 text-fw-subtle" />
            </div>
          </div>
        )}
        {showChangePassword && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onChangePassword}
            data-testid="change-password-button"
            title={collapsed ? 'Cambia password' : undefined}
            className={`w-full gap-3 text-fw-subtle hover:text-fw-yellow hover:bg-white/10 ${collapsed ? 'justify-center px-2' : 'justify-start px-3'}`}
          >
            <KeyRound className="w-5 h-5 shrink-0" />
            {!collapsed && <span>Cambia password</span>}
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={onSignOut}
          data-testid="logout-button"
          title={collapsed ? 'Esci' : undefined}
          className={`w-full gap-3 text-fw-subtle hover:text-fw-red hover:bg-fw-red/15 ${collapsed ? 'justify-center px-2' : 'justify-start px-3'}`}
        >
          <LogOut className="w-5 h-5 shrink-0" />
          {!collapsed && <span>Esci</span>}
        </Button>
      </div>

      {/* Collapse toggle (desktop only) */}
      {onToggle && (
        <div className={`py-3 border-t border-white/10 ${collapsed ? 'px-2' : 'px-3'}`}>
          <button
            onClick={onToggle}
            className={`w-full flex items-center rounded-lg py-2 text-fw-subtle hover:text-fw-yellow hover:bg-white/10 transition-colors duration-200 ${collapsed ? 'justify-center px-2' : 'gap-3 px-3'}`}
            title={collapsed ? 'Espandi sidebar' : 'Comprimi sidebar'}
          >
            {collapsed ? <ChevronsRight className="w-5 h-5" /> : <ChevronsLeft className="w-5 h-5" />}
            {!collapsed && <span className="text-sm">Comprimi</span>}
          </button>
        </div>
      )}
    </div>
  );
}

function ChangePasswordModal({ onClose, changePassword, onSuccess }) {
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    if (newPwd.length < 6) { setErr('La nuova password deve essere di almeno 6 caratteri'); return; }
    if (newPwd !== confirmPwd) { setErr('Le password non coincidono'); return; }
    if (newPwd === currentPwd) { setErr('La nuova password deve essere diversa da quella attuale'); return; }
    setLoading(true);
    try {
      await changePassword(currentPwd, newPwd);
      onSuccess();
    } catch (e2) {
      const code = e2.code || '';
      if (code.includes('wrong-password') || code.includes('invalid-credential')) setErr('Password attuale non corretta');
      else if (code.includes('weak-password')) setErr('La nuova password è troppo debole (min 6 caratteri)');
      else if (code.includes('requires-recent-login')) setErr('Per motivi di sicurezza, esegui di nuovo il login e riprova');
      else setErr('Errore durante il cambio password. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" data-testid="change-password-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="font-heading text-xl font-bold text-fw-dark mb-2">Cambia password</h2>
        <p className="text-sm text-fw-muted mb-4">Inserisci la password attuale e scegline una nuova.</p>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-sm font-medium text-fw-dark block mb-1.5">Password attuale</label>
            <input type="password" value={currentPwd} onChange={(e) => setCurrentPwd(e.target.value)} required
              className="block w-full h-10 rounded-md border border-fw-border px-3 text-sm shadow-sm focus:border-fw-yellow focus:ring-1 focus:ring-fw-yellow outline-none"
              data-testid="current-pwd-input" />
          </div>
          <div>
            <label className="text-sm font-medium text-fw-dark block mb-1.5">Nuova password</label>
            <input type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} required minLength={6}
              className="block w-full h-10 rounded-md border border-fw-border px-3 text-sm shadow-sm focus:border-fw-yellow focus:ring-1 focus:ring-fw-yellow outline-none"
              placeholder="Minimo 6 caratteri" data-testid="new-pwd-input" />
          </div>
          <div>
            <label className="text-sm font-medium text-fw-dark block mb-1.5">Conferma nuova password</label>
            <input type="password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} required minLength={6}
              className="block w-full h-10 rounded-md border border-fw-border px-3 text-sm shadow-sm focus:border-fw-yellow focus:ring-1 focus:ring-fw-yellow outline-none"
              data-testid="confirm-pwd-input" />
          </div>
          {err && <div className="p-3 bg-red-50 border border-fw-red/20 rounded-md text-sm text-fw-red">{err}</div>}
          <div className="flex gap-2 justify-end pt-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid="change-pwd-cancel-btn">Annulla</Button>
            <Button type="submit" disabled={loading} className="bg-fw-yellow hover:bg-fw-yellow-hover text-fw-dark" data-testid="change-pwd-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Conferma'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, role, userProfile, signOut, changePassword, isPasswordProvider } = useAuth();
  const [helpOpen, setHelpOpen] = useState(false);
  const permissions = usePermissions();
  const { newCount, markAllSeen } = useNewBadge();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [changePwdOpen, setChangePwdOpen] = useState(false);
  const [changePwdSuccess, setChangePwdSuccess] = useState(false);

  const handleSignOut = async () => {
    setMobileOpen(false);
    await signOut();
    navigate('/login');
  };

  const openChangePwd = () => { setMobileOpen(false); setChangePwdSuccess(false); setChangePwdOpen(true); };
  const handlePwdChanged = async () => {
    setChangePwdOpen(false);
    setChangePwdSuccess(true);
    // Forziamo logout per sicurezza: l'utente dovrà rientrare con la nuova password
    setTimeout(async () => {
      await signOut();
      navigate('/login');
    }, 1500);
  };

  const showChangePassword = isPasswordProvider?.() === true;

  const closeMobile = () => setMobileOpen(false);

  return (
    <div className="min-h-screen flex bg-fw-light">
      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 bg-fw-dark z-40 transition-all duration-300 ${collapsed ? 'lg:w-16' : 'lg:w-60'}`}
        data-testid="main-header"
      >
        <SidebarContent
          user={user}
          role={role}
          permissions={permissions}
          newCount={newCount}
          onMarkSeen={markAllSeen}
          onSignOut={handleSignOut}
          onChangePassword={openChangePwd}
          showChangePassword={showChangePassword}
          collapsed={collapsed}
          onToggle={() => setCollapsed((c) => !c)}
        />
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={closeMobile} />
          <aside className="fixed inset-y-0 left-0 w-60 bg-fw-dark shadow-xl">
            <button
              onClick={closeMobile}
              className="absolute top-4 right-4 text-fw-subtle hover:text-white"
              aria-label="Chiudi menu"
            >
              <X className="w-5 h-5" />
            </button>
            <SidebarContent user={user} role={role} permissions={permissions} newCount={newCount} onMarkSeen={markAllSeen} onSignOut={handleSignOut} onChangePassword={openChangePwd} showChangePassword={showChangePassword} onNavClick={closeMobile} collapsed={false} />
          </aside>
        </div>
      )}

      {/* Main content area */}
      <div className={`flex-1 transition-all duration-300 ${collapsed ? 'lg:pl-16' : 'lg:pl-60'}`}>
        {/* Mobile top bar */}
        <header className="sticky top-0 z-30 lg:hidden bg-white/90 backdrop-blur-md border-b border-fw-border">
          <div className="flex items-center justify-between h-14 px-4">
            <button
              onClick={() => setMobileOpen(true)}
              className="text-fw-muted hover:text-fw-dark"
              aria-label="Apri menu"
            >
              <Menu className="w-6 h-6" />
            </button>
            <NavLink to="/dashboard" className="flex items-center gap-2">
              <span className="font-heading font-bold text-lg text-fw-dark">ETO Support</span>
            </NavLink>
            <div className="w-6" />
          </div>
        </header>

        <main className="px-4 sm:px-6 lg:px-8 py-6">
          {/* Tasto help (sinistra) + loghi HQ + Fastweb (destra) */}
          <div className="flex justify-between items-center mb-4">
            <button
              onClick={() => setHelpOpen(true)}
              className="flex items-center gap-1.5 text-sm text-fw-muted hover:text-fw-dark transition-colors"
              title="Serve aiuto?"
              data-testid="help-button"
            >
              <HelpCircle className="w-5 h-5" />
              <span className="hidden sm:inline">Aiuto</span>
            </button>
            <div className="flex items-center gap-4">
              <img src="/Logo_HQ.png" alt="HQ Engineering Italia" className="h-10 object-contain" />
              <img src="/Logo_Fastweb.png" alt="Fastweb + Vodafone" className="h-24 object-contain rounded" />
            </div>
          </div>
          <Outlet />
        </main>
      </div>

      <HelpModal
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        context={role}
        userEmail={user?.email}
        isEto={userProfile?.isEto === true}
      />

      {changePwdOpen && (
        <ChangePasswordModal
          onClose={() => setChangePwdOpen(false)}
          changePassword={changePassword}
          onSuccess={handlePwdChanged}
        />
      )}

      {changePwdSuccess && (
        <div className="fixed bottom-6 right-6 z-[70] bg-green-600 text-white px-4 py-3 rounded-md shadow-lg text-sm" data-testid="pwd-changed-toast">
          Password aggiornata. Verrai disconnesso a breve per motivi di sicurezza.
        </div>
      )}
    </div>
  );
}